import { Button, FileDrop, Flex, Menu, Text, TextField, ToggleButton, toaster } from 'components'
import { useEffect, useState } from 'react'
import {
  WorkBook,
  acceptedSpreadsheets,
  readSpreadsheet,
  sheetToJson,
  useEntevatePermission,
} from 'utils'
import { useEvent } from '../../../utils/eventUtils'
import { useSessionImport } from './SessionImportProvider'

export default function SessionImportInit() {
  const [entevatePermission] = useEntevatePermission()
  const { event } = useEvent()
  const {
    appTitle,
    appType,
    data,
    momentId,
    selectedTab,
    setAppTitle,
    setAppType,
    setData,
    setMomentId,
    setSelectedTab,
    setSpreadsheet,
    spreadsheet,
  } = useSessionImport()
  const [workbook, setWorkbook] = useState<WorkBook>()

  const fileName = spreadsheet?.file.name
  const { SheetNames, Sheets } = workbook || {}
  const worksheet = Sheets?.[selectedTab]
  const explorerMoments = event?.moments.filter(m => m.type === 'explorer') || []

  useEffect(() => {
    if (!spreadsheet?.buffer) return

    readSpreadsheet(spreadsheet.buffer).then(setWorkbook)
  }, [spreadsheet])

  useEffect(() => {
    if (!selectedTab && SheetNames?.length === 1) setSelectedTab(SheetNames?.[0])
  }, [selectedTab, SheetNames])

  useEffect(() => {
    if (!worksheet) return
    ;(async () => {
      const data = await sheetToJson(worksheet).then(res =>
        res.map(({ app, App, ...rest }) => rest)
      )
      setData(data)
    })()
  }, [worksheet, setData])

  async function handleSpreadsheet(files: File[]) {
    try {
      if (!files?.length) throw 'File type not accepted'

      const file = files[0]
      const buffer = await file.arrayBuffer()
      if (!buffer) throw 'File type not accepted'

      setSelectedTab('')
      setData(undefined)
      setSpreadsheet({ file, buffer })
    } catch (error: any) {
      console.error(error)
      toaster.create({ title: error.message, type: 'error' })
    }
  }

  return (
    <Flex px={6} flexDirection="column" gap={4}>
      {entevatePermission === 'entevate' && (
        <ToggleButton
          label="App Type"
          alignSelf="flex-start"
          value={appType}
          options={['custom', 'explorer']}
          onChange={setAppType}
        />
      )}
      {appType === 'custom' ? (
        <TextField
          label="App Title"
          value={appTitle}
          borderRadius="sm"
          onChange={e => setAppTitle(e.target.value)}
          helperText="The name of the application associated with the leads"
        />
      ) : (
        <Menu
          disabled={!explorerMoments.length}
          label="Moment"
          placeholder={explorerMoments.length ? 'Select Moment' : 'No Moments Available'}
          onChange={id => setMomentId(id)}
          options={explorerMoments.map(m => ({ value: m.id, label: m.title }))}
          value={momentId}
          variant="outline"
        />
      )}
      <FileDrop onDrop={handleSpreadsheet} options={{ accept: acceptedSpreadsheets }}>
        {({ open }) => (
          <Flex
            p={3}
            border={`1px ${fileName ? 'solid' : 'dashed'} gray`}
            align="center"
            justifyContent="center"
            borderRadius="sm"
            gap={2}
          >
            <Button onClick={open} borderRadius="sm" palette="primary" size="sm">
              {SheetNames?.length ? 'Replace File' : 'Upload File'}
            </Button>
            <Text textStyle="bodySmall" fontWeight="normal" color="gray.500">
              {fileName || 'or drop a file here'}
            </Text>
          </Flex>
        )}
      </FileDrop>
      <Flex gap={2}>
        <Menu
          alignSelf="flex-start"
          disabled={!SheetNames?.length}
          label="Tab Name"
          borderRadius="sm"
          onChange={setSelectedTab}
          options={SheetNames?.map((value: any) => ({ value }))}
          value={selectedTab}
          variant="outline"
        />
        {data?.length ? (
          <Text textStyle="bodySmall" color="gray.500" paddingTop="36px">
            {data.length} rows
          </Text>
        ) : null}
      </Flex>
    </Flex>
  )
}
